// ─── useChatStream — consommation du flux SSE des agents (story 5.5) ─────────
// Décision PO Q1 (option B) : hook maison à la surface « useChat-like »
// (`messages`, `setMessages`, `status`, `error`, `sendMessage`, `stop`) pour
// qu'un passage futur à `@ai-sdk/react` ne réécrive QUE ce fichier.
// Le contexte AgentsContext est son unique consommateur.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { toast } from 'sonner'
import { apiFetchStream, isApiError } from '@/lib/api'
import { applyChatEvent, settleRunningTools } from '@/lib/chatMapping'
import { createSSEParser } from '@/lib/sseParser'
import { dedupeSuggestions } from '@/lib/agentSuggestions'
import {
  createLocalId,
  type AgentChatMessage,
  type ChatStreamStatus,
} from '@/lib/chatTypes'

export const STREAM_INTERRUPTED_MESSAGE = "La connexion avec l'agent a été interrompue."

export type ChatStreamFinishInfo = {
  conversationId: string
  message: AgentChatMessage | null
  // Suggestions reçues via `suggestion_pills` pendant la réponse (null sinon).
  suggestions: string[] | null
}

export type UseChatStreamOptions = {
  // Appelé pour chaque `tool_result` reçu EN DIRECT (jamais sur l'historique).
  onToolResult?: (name: string, result: unknown) => void
  // Appelé après `[DONE]` (réponse complète, y compris avec un event `error`).
  onFinish?: (info: ChatStreamFinishInfo) => void
  // Appelé à la fin de CHAQUE envoi (DONE, stop, abort, flux coupé, erreur),
  // dès que l'id de conversation a été résolu. Permet de rafraîchir la liste
  // même quand la réponse n'est pas allée au bout (conversation paresseuse).
  onSettled?: (info: ChatStreamSettledInfo) => void
}

export type ChatStreamSettledInfo = {
  conversationId: string
  // true si `[DONE]` a été reçu (onFinish a alors été appelé).
  completed: boolean
}

export type SendMessageOptions = {
  // Id de conversation, ou résolveur asynchrone (création paresseuse) exécuté
  // APRÈS l'ajout optimiste du message utilisateur.
  conversationId: string | (() => Promise<string>)
  // Story 7.3 : pills actuellement affichées (le backend ne les re-propose pas).
  previousSuggestions?: string[]
}

export type UseChatStreamResult = {
  messages: AgentChatMessage[]
  setMessages: Dispatch<SetStateAction<AgentChatMessage[]>>
  status: ChatStreamStatus
  error: string | null
  suggestions: string[] | null
  sendMessage: (text: string, options: SendMessageOptions) => Promise<void>
  stop: () => void
}

export function useChatStream(options: UseChatStreamOptions = {}): UseChatStreamResult {
  const [messages, setMessages] = useState<AgentChatMessage[]>([])
  const [status, setStatus] = useState<ChatStreamStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<string[] | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Callbacks lus via ref : jamais de closure périmée dans la boucle de lecture.
  const optionsRef = useRef(options)
  useEffect(() => {
    optionsRef.current = options
  })

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  // Abort au démontage (jamais de flux orphelin).
  useEffect(() => () => abortRef.current?.abort(), [])

  const sendMessage = useCallback(
    async (text: string, { conversationId, previousSuggestions }: SendMessageOptions) => {
      const content = text.trim()
      if (!content) return

      // Jamais deux flux parallèles.
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      const isCurrent = () => abortRef.current === controller

      const nowIso = new Date().toISOString()
      const userId = createLocalId()
      const assistantId = createLocalId()
      setMessages((prev) => [
        ...prev,
        { id: userId, role: 'user', content, createdAt: nowIso, status: 'sending' },
        { id: assistantId, role: 'assistant', content: '', createdAt: nowIso, status: 'streaming' },
      ])
      setStatus('submitted')
      setError(null)
      setSuggestions(null)

      const updateMessage = (id: string, fn: (m: AgentChatMessage) => AgentChatMessage) =>
        setMessages((prev) => prev.map((m) => (m.id === id ? fn(m) : m)))

      let resolvedId = typeof conversationId === 'string' ? conversationId : ''
      let gotDone = false
      let sseError: string | null = null
      let streamSuggestions: string[] | null = null
      let streamOpened = false
      // Copie locale du message assistant (pour onFinish sans relire le state).
      let assistantSnapshot: AgentChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        createdAt: nowIso,
        status: 'streaming',
      }
      const applyToAssistant = (fn: (m: AgentChatMessage) => AgentChatMessage) => {
        assistantSnapshot = fn(assistantSnapshot)
        updateMessage(assistantId, fn)
      }

      try {
        if (typeof conversationId !== 'string') resolvedId = await conversationId()
        if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')

        const res = await apiFetchStream(`/chat/${encodeURIComponent(resolvedId)}/stream`, {
          method: 'POST',
          body: JSON.stringify({
            message: content,
            ...(previousSuggestions && previousSuggestions.length > 0 ? { previousSuggestions } : {}),
          }),
          signal: controller.signal,
        })
        streamOpened = true
        updateMessage(userId, (m) => ({ ...m, status: 'sent' }))
        if (isCurrent()) setStatus('streaming')

        const reader = res.body?.getReader()
        if (!reader) throw new Error('Flux de réponse absent')
        const decoder = new TextDecoder()
        const parser = createSSEParser(
          (event) => {
            if (event.type === 'suggestion_pills') {
              streamSuggestions = dedupeSuggestions(event.suggestions).slice(0, 3)
              setSuggestions(streamSuggestions)
              return
            }
            if (event.type === 'error') sseError = event.error
            applyToAssistant((m) => applyChatEvent(m, event))
            if (event.type === 'tool_result') {
              optionsRef.current.onToolResult?.(event.name, event.result)
            }
          },
          () => {
            gotDone = true
          }
        )

        while (!gotDone) {
          const { done, value } = await reader.read()
          if (done) break
          parser.push(decoder.decode(value, { stream: true }))
        }
        if (!gotDone) {
          parser.push(decoder.decode())
          parser.flush()
        } else {
          // Libère la connexion sans attendre la fermeture serveur.
          reader.cancel().catch(() => {})
        }

        if (!gotDone) {
          if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')
          // Corps terminé sans [DONE] → flux coupé.
          applyToAssistant((m) =>
            settleRunningTools({ ...m, status: 'error', errorMessage: STREAM_INTERRUPTED_MESSAGE })
          )
          if (isCurrent()) {
            setError(STREAM_INTERRUPTED_MESSAGE)
            setStatus('error')
          }
          return
        }

        applyToAssistant((m) =>
          settleRunningTools({ ...m, status: sseError ? 'error' : 'done' })
        )
        if (isCurrent()) {
          setError(sseError)
          setStatus(sseError ? 'error' : 'idle')
        }
        optionsRef.current.onFinish?.({
          conversationId: resolvedId,
          message: assistantSnapshot,
          suggestions: streamSuggestions,
        })
      } catch (e: unknown) {
        // Abort (stop / changement de conversation / nouvel envoi / démontage) :
        // ce n'est PAS une erreur — on garde le texte partiel.
        if (controller.signal.aborted) {
          // Abort AVANT l'ouverture du flux : le message n'a jamais atteint
          // l'agent → état `error` (renvoyable), jamais `sent`.
          if (!streamOpened) updateMessage(userId, (m) => ({ ...m, status: 'error' }))
          setMessages((prev) =>
            prev.flatMap((m) => {
              if (m.id !== assistantId) return [m]
              const settled = settleRunningTools({ ...m, status: 'done' })
              const isEmpty =
                !settled.content && !settled.toolInvocations?.length && !settled.actionCards?.length
              return isEmpty ? [] : [settled]
            })
          )
          if (isCurrent()) setStatus('idle')
          return
        }

        const message =
          isApiError(e) && !streamOpened ? e.message : STREAM_INTERRUPTED_MESSAGE
        if (!streamOpened) {
          updateMessage(userId, (m) => ({ ...m, status: 'error' }))
          if (isApiError(e)) toast.error(message)
        }
        applyToAssistant((m) => settleRunningTools({ ...m, status: 'error', errorMessage: message }))
        if (isCurrent()) {
          setError(message)
          setStatus('error')
        }
      } finally {
        if (isCurrent()) abortRef.current = null
        if (resolvedId) {
          optionsRef.current.onSettled?.({ conversationId: resolvedId, completed: gotDone })
        }
      }
    },
    []
  )

  return { messages, setMessages, status, error, suggestions, sendMessage, stop }
}
